import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import GenderDetail from "../components/ModelDetails/GenderDetail";
import CareEconomyDetail from "../components/ModelDetails/CareEconomyDetail";
import MentalHealthDetail from "../components/ModelDetails/MentalHealthDetail";
import ClimateChangeDetail from "../components/ModelDetails/ClimateChangeDetail";
import TrendsDetail from "../components/ModelDetails/TrendsDetail";
import Loader from "../components/ui/Loader";
import Icon from "../components/ui/Icon";
import ScrollToTop from "../components/ui/ScrollToTop";

const tabs = [
  {
    id: "gender",
    label: "GENDER AND CAREGIVING",
  },
  {
    id: "care-economy",
    label: "THE CARE ECONOMY",
  },
  {
    id: "mental-health",
    label: "CAREGIVING AND MENTAL HEALTH",
  },
  {
    id: "climate-change",
    label: "CLIMATE CHANGE AND CAREGIVING",
  },
  {
    id: "trends",
    label: "EMERGING TRENDS IN CAREGIVING",
  },
];

const TabsPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab");
    if (tabParam && tabs.find((tab) => tab.id === tabParam)) {
      return tabParam;
    }
    return "gender";
  });
  const [isTabLoading, setIsTabLoading] = useState(true);
  const contentRef = useRef(null);

  // Update active tab based on query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab");
    if (tabParam && tabs.find((tab) => tab.id === tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Show loader when tab changes
  useEffect(() => {
    setIsTabLoading(true);

    let imageLoadHandlers = [];
    let isComplete = false;
    let fallbackTimer = null;

    // Wait for content to render and images to load
    const timer = setTimeout(() => {
      // Check if images are loaded
      const images = contentRef.current?.querySelectorAll("img");

      if (images && images.length > 0) {
        let loadedCount = 0;
        const totalImages = images.length;

        const checkImagesLoaded = () => {
          loadedCount++;
          if (loadedCount === totalImages && !isComplete) {
            isComplete = true;
            setIsTabLoading(false);
            // Clean up event listeners
            imageLoadHandlers.forEach(({ img, loadHandler, errorHandler }) => {
              img.removeEventListener("load", loadHandler);
              img.removeEventListener("error", errorHandler);
            });
            if (fallbackTimer) clearTimeout(fallbackTimer);
          }
        };

        images.forEach((img) => {
          if (img.complete) {
            checkImagesLoaded();
          } else {
            const loadHandler = () => checkImagesLoaded();
            const errorHandler = () => checkImagesLoaded();
            img.addEventListener("load", loadHandler);
            img.addEventListener("error", errorHandler);
            imageLoadHandlers.push({ img, loadHandler, errorHandler });
          }
        });

        // Fallback: hide loader after max wait time
        fallbackTimer = setTimeout(() => {
          if (!isComplete) {
            isComplete = true;
            setIsTabLoading(false);
            // Clean up event listeners
            imageLoadHandlers.forEach(({ img, loadHandler, errorHandler }) => {
              img.removeEventListener("load", loadHandler);
              img.removeEventListener("error", errorHandler);
            });
          }
        }, 2000);
      } else {
        // No images, just wait a bit for content to render
        setTimeout(() => {
          setIsTabLoading(false);
        }, 300);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (fallbackTimer) clearTimeout(fallbackTimer);
      // Clean up any remaining event listeners
      imageLoadHandlers.forEach(({ img, loadHandler, errorHandler }) => {
        img.removeEventListener("load", loadHandler);
        img.removeEventListener("error", errorHandler);
      });
    };
  }, [activeTab]);

  // Scroll to top when tab changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeTab]);

  const handleTabClick = (tab) => {
    if (tab.id !== activeTab) {
      setIsTabLoading(true);
      setActiveTab(tab.id);
      // Update URL without navigating away
      window.history.pushState({}, "", `/tabs?tab=${tab.id}`);
    }
  };

  // Render content based on active tab
  const renderTabContent = () => {
    const detailProps = { isTabLoading };
    switch (activeTab) {
      case "gender":
        return <GenderDetail {...detailProps} />;
      case "care-economy":
        return <CareEconomyDetail {...detailProps} />;
      case "mental-health":
        return <MentalHealthDetail {...detailProps} />;
      case "climate-change":
        return <ClimateChangeDetail {...detailProps} />;
      case "trends":
        return <TrendsDetail {...detailProps} />;
      default:
        return <GenderDetail {...detailProps} />;
    }
  };

  return (
    <main className="container mx-auto px-3 lg:px-5">
      {isTabLoading && <Loader />}

      {/* Navigation Tabs */}
      <nav className="fixed top-0 left-0 right-0 z-[200] bg-white">
        <div className="">
          <div className="flex items-center justify-between gap-3 lg:gap-5 py-4 bg-white">
            {/* Home Icon */}
            <div className="ps-3 lg:ps-4">
              <Link to="/">
                <Icon src="/assets/svgs/home_button.svg" alt="Back to home" />
              </Link>
            </div>

            {/* Tab Buttons */}
            <div className="flex items-center justify-between gap-2 md:gap-4 overflow-x-auto">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab)}
                    className={`flex-shrink-0 px-3 py-3 lg:py-3.5 border-2 border-softBlack rounded-full font-medium text-xs uppercase whitespace-nowrap transition-all duration-200 ${
                      isActive
                        ? "bg-softBlack text-white"
                        : "bg-white text-softBlack hover:bg-softBlack hover:text-white"
                    }`}
                  >
                    <span className="tracking-widest">{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <div></div>
          </div>
        </div>
      </nav>

      {/* Content Area */}
      <div className="pt-16 pb-8">
        <div className="relative">
          <div className="flex relative">
            {/* Main Content */}
            <div className="flex-1" ref={contentRef}>
              {renderTabContent()}
            </div>

            {/* Right Side SVGs */}
            <div className="absolute right-2 xxl:right-5 top-10 h-fit flex-col items-center hidden lg:flex">
              {/* Country image at top */}
              <img
                src="/assets/svgs/countrys.svg"
                alt="Countries"
                className="w-auto h-auto [zoom:0.8] 1xl:[zoom:1]"
              />
            </div>
          </div>
        </div>
      </div>
      <ScrollToTop />
    </main>
  );
};

export default TabsPage;
